// components/Footer.js
// Mobile: 4-column grid stacks to 2 columns on tablet, 1 column on phone

import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();

  const quickLinks = [
    { to: '/about',        label: 'About Us' },
    { to: '/process',      label: 'Our Process' },
    { to: '/services',     label: 'Services' },
    { to: '/gallery',      label: 'Portfolio' },
    { to: '/testimonials', label: 'Testimonials' },
    { to: '/contact',      label: 'Contact' },
  ];

  const services = [
    'Kitchen Design', 'Bespoke Cabinetry', 'Installation',
    'Project Management', 'Appliance Selection', 'Lighting Design',
  ];

  return (
    <footer style={s.footer}>
      <div className="container">

        {/* Responsive grid — handled via a style tag injected into the page */}
        <style>{`
          .footer-grid {
            display: grid;
            grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
            gap: 4rem;
            margin-bottom: 3rem;
          }
          @media (max-width: 900px) {
            .footer-grid {
              grid-template-columns: 1fr 1fr;
              gap: 2.5rem;
            }
          }
          @media (max-width: 480px) {
            .footer-grid {
              grid-template-columns: 1fr;
              gap: 2rem;
            }
          }
        `}</style>

        <div className="footer-grid">

          {/* Brand */}
          <div>
            <div style={s.logoRow} onClick={() => navigate('/')}>
              <img
                src="/images/Urban-Logo.png"
                alt="Urban Kitchens"
                style={{ height: '60px', width: 'auto', display: 'block' }}
              />
            </div>
            <p style={s.tagline}>
              Crafted in Belfast. Built for Life.<br />
              Premium kitchen design for discerning homeowners across Northern Ireland.
            </p>
            <div style={s.socialRow}>
              {/* Instagram */}
              <a href="https://www.instagram.com/urbankdcni/" target="_blank" rel="noreferrer" style={s.socialLink} title="Instagram">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
                      <stop offset="0%" stopColor="#fdf497"/>
                      <stop offset="5%" stopColor="#fdf497"/>
                      <stop offset="45%" stopColor="#fd5949"/>
                      <stop offset="60%" stopColor="#d6249f"/>
                      <stop offset="90%" stopColor="#285AEB"/>
                    </radialGradient>
                  </defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="url(#ig-grad)"/>
                  <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
                  <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
                </svg>
              </a>

              {/* Facebook */}
              <a href="https://facebook.com" target="_blank" rel="noreferrer" style={s.socialLink} title="Facebook">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="5" fill="#1877F2"/>
                  <path d="M16 8h-2a1 1 0 00-1 1v2h3l-.5 3H13v7h-3v-7H8v-3h2V9a4 4 0 014-4h2v3z" fill="white"/>
                </svg>
              </a>

              {/* LinkedIn */}
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" style={s.socialLink} title="LinkedIn">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                  <path d="M7 9h2v8H7zM8 8a1.2 1.2 0 110-2.4A1.2 1.2 0 018 8zM11 9h2v1.1c.4-.7 1.2-1.2 2.2-1.2C17 8.9 18 10 18 12.2V17h-2v-4.5c0-1-.4-1.6-1.3-1.6-.9 0-1.7.6-1.7 1.8V17h-2V9z" fill="white"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={s.colHead}>Quick Links</h4>
            <ul style={s.list}>
              {quickLinks.map(l => (
                <li key={l.to} style={s.listItem}>
                  <span style={s.footerLink} onClick={() => navigate(l.to)}>{l.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 style={s.colHead}>Services</h4>
            <ul style={s.list}>
              {services.map(svc => (
                <li key={svc} style={s.listItem}>
                  <span style={s.footerLink} onClick={() => navigate('/services')}>{svc}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 style={s.colHead}>Get in Touch</h4>
            {[
              { icon: '📞', text: '028 9013 2146',          sub: 'Main Office' },
              { icon: '📱', text: '079 1241 9641',          sub: 'Mobile' },
              { icon: '✉',  text: 'hello@urbankdcni.co.uk', sub: null },
              { icon: '📍', text: '122f Upper Lisburn Road\nBelfast, BT10 0BD', sub: null },
            ].map((item, i) => (
              <div key={i} style={s.contactItem}>
                <span style={s.contactIcon}>{item.icon}</span>
                <div>
                  <div style={s.contactText}>{item.text}</div>
                  {item.sub && <div style={s.contactSub}>{item.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={s.bottom}>
          <span>© 2026 Urban Kitchens. All rights reserved.</span>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.72rem' }}>Bespoke Kitchen Design Belfast</span>
        </div>
      </div>
    </footer>
  );
}

const s = {
  footer: { background: 'var(--bg2)', borderTop: '1px solid var(--border)', padding: '60px 0 30px' },
  logoRow: { display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '1rem' },
  logoU: { fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem', color: 'var(--orange)' },
  logoText: { display: 'flex', flexDirection: 'column' },
  logoRban: { fontFamily: "'Manrope', sans-serif", fontSize: '1rem', color: 'var(--text)' },
  logoKitchens: { fontFamily: "'Manrope', sans-serif", fontSize: '0.55rem', letterSpacing: '0.3em', color: 'var(--text-muted)', textTransform: 'uppercase' },
  tagline: { color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '1.5rem', fontWeight: 300 },
  socialRow: { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  socialLink: { display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s', opacity: 0.9 },
  socialBtn: { width: 38, height: 38, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', color: 'var(--text-muted)', cursor: 'pointer' },
  colHead: { fontSize: '0.82rem', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '1.5rem' },
  list: { listStyle: 'none' },
  listItem: { marginBottom: '0.85rem' },
  footerLink: { color: 'var(--text-muted)', fontSize: '1rem', cursor: 'pointer', fontWeight: 300 },
  contactItem: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' },
  contactIcon: { color: 'var(--orange)', fontSize: '1rem', marginTop: 2, flexShrink: 0 },
  contactText: { fontSize: '1rem', color: 'var(--text-muted)', lineHeight: 1.6, whiteSpace: 'pre-line', fontWeight: 300 },
  contactSub: { fontSize: '0.85rem', color: 'var(--text-dim)', fontWeight: 300 },
  bottom: { borderTop: '1px solid var(--border)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem', color: 'var(--text-dim)', flexWrap: 'wrap', gap: '0.5rem', fontWeight: 300 },
};
